/**
 * Abbreviates a full name by shortening middle surnames when the name exceeds 25 characters.
 * Keeps the first name and last surname intact.
 * 
 * Examples:
 * - "Arnaldo Quintella de Oliveira Celestino" -> "Arnaldo Q. de O. Celestino"
 * - "Antonio Figueiroa Amâncio de Souza e Silva"-> "Antonio F. A. de S. e Silva" * -"João Silva" -> "João Silva" (no change if <= 25 chars)
 * 
 * @param {string} fullName - The full name to abbreviate
 * @returns {string} - The abbreviated name or original if <= 25 chars
 */
export const abbreviateLongName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') {
    return fullName;
  }

  const trimmedName = fullName?.trim();
  
  // If name is 25 characters or less, return as-is
  if (trimmedName?.length <= 25) {
    return trimmedName;
  }

  // Split name into parts
  const nameParts = trimmedName?.split(/\s+/);
  
  // If only one or two parts, return as-is (no middle names to abbreviate)
  if (nameParts?.length <= 2) {
    return trimmedName;
  }

  // Keep first name and last surname intact
  const firstName = nameParts?.[0];
  const lastName = nameParts?.[nameParts?.length - 1];
  
  // Abbreviate all middle parts
  const middleParts = nameParts?.slice(1, -1);
  const abbreviatedMiddle = middleParts?.map(part => {
    // Keep connecting words (de, da, do, das, dos, e) lowercase and unabbreviated
    const lowerPart = part?.toLowerCase();
    if (['de', 'da', 'do', 'das', 'dos', 'e']?.includes(lowerPart)) {
      return lowerPart;
    }
    // Abbreviate other parts (take first letter + dot)
    return part?.charAt(0)?.toUpperCase() + '.';
  })?.join(' ');

  // Construct abbreviated name
  return `${firstName} ${abbreviatedMiddle} ${lastName}`;
};